import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { AutomationFormErrorType } from '~/enums/AutomationFormErrorType';
import { QueryParamType } from '~/enums/QueryParamType';
import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { QueryFiltersFormField } from '~/features/advanced-search/query-filters/QueryFiltersFormField';
import { QueryScope } from '~/features/advanced-search/query-filters/QueryScope';
import { useRestrictedFilterIds } from '~/features/quick-ai/hooks';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { ERROR } from '~/types/toast.types';
import Alert from '~/ui/components/Alert/Alert';
import InputLabel from '~/ui/components/InputLabel/InputLabel';
import Link from '~/ui/components/Link/Link';
import Text from '~/ui/components/Text/Text';

import { SAMPLE_MIN_WORD_COUNT_BULK_TAGGING } from '../../constants';
import { isSampleNameChanged } from '../../util';

const QuickAiForm = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const formatDate = useFormatDate();

  const [clauseOptions, setClauseOptions] = useState([]);
  const [showErrorAlert, setShowErrorAlert] = useState(true);
  const [selectedQuery, setSelectedQuery] = useState([]);
  const clause = useSelector(
    slices.automation.selectors.selectAutomationClause,
  );
  const quickAiForm = useSelector(
    slices.automation.selectors.selectAutomationQuickAiForm,
  );
  const isOnClausePage = isEmpty(clause.name);
  const isUpdating = !!quickAiForm.id;

  const doesSampleNameOverLimit = (quickAiForm.sampleName.length ?? 0) > 100;
  const doesClauseNameOverLimit = (quickAiForm.name?.length ?? 0) > 500;
  const isSampleContentDuplicated = !!quickAiForm.errors?.includes(
    AutomationFormErrorType.DupContent,
  );
  const isSampleNameDuplicated = !!quickAiForm.errors?.includes(
    AutomationFormErrorType.DupSampleName,
  );
  const enableQuickAiFilters = useFlag(FlagType.QuickAiFilters);

  const restrictedClauseFilterIds = useRestrictedFilterIds();

  const getSampleName = (name) => {
    const timestamp = formatDate(new Date(), 'iso_datetime');
    return `${name.substring(0, 20)}_${timestamp}`;
  };

  useEffect(() => {
    if (quickAiForm.doesClauseExist) {
      setShowErrorAlert(true);
    }
  }, [quickAiForm.name]);

  useEffect(() => {
    const doesSampleNameExist = quickAiForm.sampleName.length ?? 0;
    if (!doesSampleNameExist) {
      const sampleName = !!clause.name ? getSampleName(clause.name) : '';
      dispatch(
        slices.automation.actions.setAutomationQuickAiDataForm({
          ...quickAiForm,
          name: clause.name,
          sampleName,
        }),
      );
    }
  }, [clause]);

  const { data, error } = api.endpoints.getClauseSuggestions.useQuery();
  const { data: existingClause = [] } =
    api.endpoints.getIdentifications.useQuery(
      {
        name: quickAiForm.name,
      },
      {
        skip: !quickAiForm.name,
      },
    );

  useEffect(() => {
    if (error) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'QuickAiForm.loadClauseError',
          defaultMessage: 'Failed to load clause. Please try again.',
          description: 'Error message when clause fails to load',
        }),
      );
    }

    const options = [];

    if (clause.name) {
      options.push({ label: clause.name, value: clause.name });
    }

    if (data) {
      data.suggestions.forEach((v) => {
        if (clause.name !== v) options.push({ label: v, value: v });
      });
    }

    setClauseOptions(options);
  }, [data, error]);

  useEffect(() => {
    if (!quickAiForm.doesClauseExist && enableQuickAiFilters) {
      const activeQuery = isActiveQuery(selectedQuery);

      dispatch(
        slices.automation.actions.setAutomationQuickAiDataForm({
          ...quickAiForm,
          query: activeQuery,
        }),
      );
    }
  }, [quickAiForm.doesClauseExist, selectedQuery]);

  const setCreateClause = (event) => {
    const name = event?.trim();

    const shouldGenSampleName =
      !!name && !isSampleNameChanged(quickAiForm.sampleName);
    const sampleName = shouldGenSampleName
      ? getSampleName(name)
      : quickAiForm.sampleName;

    const doesClauseExist = !!data?.suggestions?.includes(name);

    dispatch(
      slices.automation.actions.setAutomationQuickAiDataForm({
        ...quickAiForm,
        name,
        sampleName,
        doesClauseExist,
      }),
    );
  };

  const onAddProvision = (event) => {
    const suggestion = { label: event.label.trim(), value: event.value.trim() };

    setClauseOptions([...clauseOptions, suggestion]);
    setCreateClause(suggestion.value);
  };

  const setCreateContent = (content) => {
    dispatch(
      slices.automation.actions.setAutomationQuickAiDataForm({
        ...quickAiForm,
        content,
      }),
    );
  };

  const setClauseName = () => {
    dispatch(
      slices.automation.actions.setAutomationClause({
        ...clause,
        name: quickAiForm.name,
      }),
    );
  };

  const setSampleName = (name) => {
    dispatch(
      slices.automation.actions.setAutomationQuickAiDataForm({
        ...quickAiForm,
        sampleName: name?.trimStart(),
      }),
    );
  };

  const existingClauseFilters = isActiveQuery(
    existingClause?.filters ?? [],
  ).length;

  const isCreatingSample = Boolean(clause.name);

  const clauseNameParam = `${QueryParamType.Clause}=${encodeURIComponent(
    quickAiForm.name,
  )}`;

  const navigateToAutomationHub = {
    additionalText: intl.formatMessage(
      {
        id: 'QuickAiForm.existingFiltersText',
        defaultMessage:
          'This clause has {count} existing filter(s). Filters can be managed in ',
        description: 'Text for existing clause filters',
      },
      { count: existingClauseFilters },
    ),
    linkText: 'Automation Hub',
    pathname: '/automation/detail',
    search: `?&${clauseNameParam}`,
    tooltip: intl.formatMessage({
      id: 'QuickAiForm.automationHubTooltip',
      defaultMessage: 'Go to Automation Hub page',
      description: 'Tooltip for Automation Hub link',
    }),
  };

  return (
    <Layout w="750px" direction="column" spacing={8} pb="20%">
      {isOnClausePage && showErrorAlert && quickAiForm.doesClauseExist && (
        <Alert
          key="warning"
          enableIcon
          variant="danger"
          onDismiss={() => setShowErrorAlert(!showErrorAlert)}
        >
          <Layout direction="column" spacing={2}>
            <Text variant="xs-dense">
              {intl.formatMessage(
                {
                  id: 'QuickAiForm.duplicateClauseAlert',
                  defaultMessage:
                    'The {clauseName} Clause already exists. Looks like you want to create a New Sample instead of creating a New Clause.',
                  description: 'Alert text for duplicate clause name',
                },
                { clauseName: quickAiForm.name },
              )}
            </Text>
            <Link onClick={setClauseName}>
              {intl.formatMessage({
                id: 'QuickAiForm.createNewSampleLink',
                defaultMessage: 'Create a New Sample',
                description: 'Link text for creating a new sample',
              })}
            </Link>
          </Layout>
        </Alert>
      )}
      {isUpdating ? (
        <Alert key="warning" enableIcon variant="warning">
          {intl.formatMessage({
            id: 'QuickAiForm.updateWarning',
            defaultMessage:
              'Your changes will only apply to future documents. To identify a new language in existing documents, consider creating a new sample.',
            description: 'Warning for updating sample',
          })}
        </Alert>
      ) : (
        <Alert key="info" enableIcon variant="info">
          {intl.formatMessage({
            id: 'QuickAiForm.applySampleInfo',
            defaultMessage: 'Evisort will apply the Sample to all documents.',
            description: 'Info for applying sample to all documents',
          })}
        </Alert>
      )}
      <InputLabel
        isRequired
        id="clause"
        label={intl.formatMessage({
          id: 'QuickAiForm.clauseLabel',
          defaultMessage: 'Clause',
          description: 'Label for clause input',
        })}
        description={intl.formatMessage({
          id: 'QuickAiForm.clauseDescription',
          defaultMessage:
            'The Clause that this language will be associated with when identified.',
          description: 'Description for clause input',
        })}
      >
        <Select
          disabled={!isOnClausePage}
          id="select-a-clause"
          options={clauseOptions}
          placeholder={intl.formatMessage({
            id: 'QuickAiForm.clausePlaceholder',
            defaultMessage: 'Create a New Clause',
            description: 'Placeholder for clause select',
          })}
          value={quickAiForm.name}
          error={
            isOnClausePage && quickAiForm.doesClauseExist
              ? intl.formatMessage({
                  id: 'QuickAiForm.duplicateNameError',
                  defaultMessage:
                    'Duplicate name. Please select a different name.',
                  description: 'Error for duplicate clause name',
                })
              : null
          }
          onAddOption={onAddProvision}
          onChange={setCreateClause}
        />
        <Layout mt={-2}>
          {doesClauseNameOverLimit && (
            <Text
              color="red-600"
              variant="2xs-dense"
              sx={{ position: 'absolute' }}
            >
              {intl.formatMessage({
                id: 'QuickAiForm.clauseNameLimitError',
                defaultMessage:
                  'Clause name cannot be longer than 500 characters',
                description: 'Error for clause name length',
              })}
            </Text>
          )}
        </Layout>
      </InputLabel>
      {enableQuickAiFilters && !isCreatingSample && (
        <Layout preset="sections">
          <QueryFiltersFormField
            title={intl.formatMessage({
              id: 'QuickAiForm.documentFiltersTitle',
              defaultMessage: 'Document Filters',
              description: 'Title for document filters section',
            })}
            restrictedFilterIds={restrictedClauseFilterIds}
            defaultFilters={['folder', 'Contract Type', 'Effective Date']}
            description={
              !quickAiForm.doesClauseExist
                ? intl.formatMessage({
                    id: 'QuickAiForm.documentFiltersDescription',
                    defaultMessage:
                      'Filters will be used to select documents that contain this exact clause',
                    description: 'Description for document filters',
                  })
                : undefined
            }
            descriptionLink={
              quickAiForm.doesClauseExist ? navigateToAutomationHub : undefined
            }
            query={selectedQuery}
            onQueryChange={(updatedQuery) => {
              setSelectedQuery(updatedQuery);
            }}
            readOnly={quickAiForm.doesClauseExist}
          />
          <QueryScope
            query={selectedQuery}
            isVisible={selectedQuery.length > 0}
          />
        </Layout>
      )}
      <InputLabel
        isRequired
        id="sample-text"
        label={intl.formatMessage({
          id: 'QuickAiForm.sampleTextLabel',
          defaultMessage: 'Sample text',
          description: 'Label for sample text input',
        })}
        description={intl.formatMessage({
          id: 'QuickAiForm.sampleTextDescription',
          defaultMessage:
            "We'll look for language similar to this in existing and future contracts.",
          description: 'Description for sample text input',
        })}
      >
        <TextArea
          id="input-sample-text"
          name="input-sample-text"
          width="100%"
          placeholder=""
          value={quickAiForm.content}
          onChange={setCreateContent}
          rows={8}
        />
        {isSampleContentDuplicated && (
          <Text color="red-600" variant="2xs-dense" sx={{ marginTop: '-8px' }}>
            {intl.formatMessage({
              id: 'QuickAiForm.duplicateSampleTextError',
              defaultMessage:
                'Another Sample text with an identical text already exists for this clause',
              description: 'Error for duplicate sample text',
            })}
          </Text>
        )}
        {!isSampleContentDuplicated && (
          <Text color="gray-600" variant="2xs-dense" sx={{ marginTop: '-8px' }}>
            {intl.formatMessage(
              {
                id: 'QuickAiForm.sampleTextWordCountInfo',
                defaultMessage:
                  'Sample text must be at least {minWordCount} words long.',
                description: 'Info for sample text minimum word count',
              },
              { minWordCount: SAMPLE_MIN_WORD_COUNT_BULK_TAGGING },
            )}
          </Text>
        )}
      </InputLabel>
      <InputLabel
        isRequired
        id="sample-name"
        label={intl.formatMessage({
          id: 'QuickAiForm.sampleNameLabel',
          defaultMessage: 'Sample name',
          description: 'Label for sample name input',
        })}
        description={intl.formatMessage({
          id: 'QuickAiForm.sampleNameDescription',
          defaultMessage:
            'You may give this Sample a name to make it easier to search for it.',
          description: 'Description for sample name input',
        })}
      >
        <TextInput
          id="input-a-sample-name"
          width="fullWidth"
          value={quickAiForm.sampleName}
          onChange={setSampleName}
        />

        {isSampleNameDuplicated ? (
          <Text color="red-600" variant="2xs-dense" sx={{ marginTop: '-8px' }}>
            {intl.formatMessage({
              id: 'QuickAiForm.duplicateSampleNameError',
              defaultMessage:
                'Another Sample with an identical name already exists for this clause.',
              description: 'Error for duplicate sample name',
            })}
          </Text>
        ) : (
          <Text
            color={doesSampleNameOverLimit ? 'red-600' : 'gray-600'}
            variant="2xs-dense"
            sx={{ marginTop: '-8px' }}
          >
            {intl.formatMessage(
              {
                id: 'QuickAiForm.sampleNameCountInfo',
                defaultMessage: '{count} / 100 {overLimit}',
                description: 'Info for sample name character count',
              },
              {
                count: quickAiForm.sampleName.length ?? 0,
                overLimit: doesSampleNameOverLimit
                  ? intl.formatMessage({
                      id: 'QuickAiForm.sampleNameLimitError',
                      defaultMessage:
                        '(Sample name cannot be longer than 100 characters)',
                      description: 'Error for sample name length',
                    })
                  : '',
              },
            )}
          </Text>
        )}
      </InputLabel>
    </Layout>
  );
};

export default QuickAiForm;
